import { useState } from 'react';
import { DollarSign, Clock, Percent, CheckCircle2 } from 'lucide-react';
import { setTeacherRate, money } from '../../finances/api';
import { useToast } from '../../../context/ToastContext';

// CÓMO COBRA UN MAESTRO — pieza ÚNICA para verlo y editarlo.
//
// 🚨 17-sep-2026 (queja de Carlos, con razón): la ficha del maestro solo conocía UNA forma de
// pago —un precio por hora— cuando la nómina lleva desde el 15-sep soportando tres:
//   · por hora
//   · por hora CON TARIFA DE MAÑANA (Isabel Payro: $250 antes de las 12:00, $200 después)
//   · por COMISIÓN sobre lo que pagan sus alumnas (Noli Belanger, 50%)
// Resultado: a Isabel la ficha le enseñaba $200 y se callaba su tarifa de mañana, y a Noli le
// enseñaba «$0 / hora». Arreglar el caso de Noli a mano no era el arreglo: el arreglo es que
// exista UN solo sitio que sepa describir y editar CUALQUIER esquema, y que lo usen todas las
// pantallas. Si mañana aparece un cuarto modo, se añade aquí y aparece en todas partes.

export interface EsquemaPagoValor {
  pay_mode?: string | null;
  hourly_rate?: string | number | null;
  morning_rate?: string | number | null;
  morning_until?: string | null;
  commission_pct?: string | number | null;
}

const n = (v: string | number | null | undefined) => (v == null || v === '' ? null : Number(v));
const hhmm = (t?: string | null) => (t || '12:00').slice(0, 5);

/** Frase corta que describe el esquema. La usan la ficha, las listas y cualquier resumen. */
export function resumenPago(v: EsquemaPagoValor): string {
  if ((v.pay_mode || 'hourly') === 'commission') {
    return `Comisión ${Number(v.commission_pct || 0)}% de lo que pagan sus alumnas`;
  }
  const base = n(v.hourly_rate);
  if (!base) return 'Sin tarifa asignada';
  const m = n(v.morning_rate);
  return m != null
    ? `${money(m)}/h antes de las ${hhmm(v.morning_until)} · ${money(base)}/h después`
    : `${money(base)}/h`;
}

export default function EsquemaPago({ userId, valor, onSaved }: {
  userId: number; valor: EsquemaPagoValor; onSaved?: () => void;
}) {
  const toast = useToast();
  const [modo, setModo] = useState<'hourly' | 'commission'>((valor.pay_mode as any) === 'commission' ? 'commission' : 'hourly');
  const [rate, setRate] = useState(valor.hourly_rate != null ? String(Number(valor.hourly_rate) || '') : '');
  const [manana, setManana] = useState(valor.morning_rate != null ? String(Number(valor.morning_rate)) : '');
  const [hasta, setHasta] = useState(hhmm(valor.morning_until));
  const [pct, setPct] = useState(valor.commission_pct != null ? String(Number(valor.commission_pct)) : '');
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);

  async function guardar() {
    if (modo === 'commission' && !(Number(pct) > 0 && Number(pct) <= 100)) { toast.error('El porcentaje debe estar entre 1 y 100'); return; }
    if (modo === 'hourly' && rate !== '' && !(Number(rate) >= 0)) { toast.error('Tarifa inválida'); return; }
    if (modo === 'hourly' && manana !== '' && !(Number(manana) >= 0)) { toast.error('Tarifa de mañana inválida'); return; }
    setGuardando(true);
    try {
      await setTeacherRate(userId, Number(rate || 0), {
        pay_mode: modo,
        morning_rate: modo === 'hourly' && manana !== '' ? Number(manana) : null,
        morning_until: hasta || '12:00',
        commission_pct: modo === 'commission' ? Number(pct) : null,
      });
      setGuardado(true); setEditando(false);
      setTimeout(() => setGuardado(false), 1800);
      onSaved?.();
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setGuardando(false); }
  }

  const inp = 'w-24 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
  const tile = (activo: boolean) =>
    `rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${activo
      ? 'border-accent-solid bg-accent-solid/15 text-accent-ink'
      : 'border-line bg-surface text-content-2 hover:bg-surface-2'}`;

  if (!editando) {
    return (
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-line bg-canvas px-4 py-2.5">
        <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-content-3">
          {modo === 'commission' ? <Percent size={13} /> : <DollarSign size={13} />} Cómo cobra
          {guardado && <CheckCircle2 size={13} className="text-emerald-500" aria-label="Guardado" />}
        </span>
        <span className="text-sm font-semibold text-content">{resumenPago({ pay_mode: modo, hourly_rate: rate, morning_rate: manana || null, morning_until: hasta, commission_pct: pct })}</span>
        <button type="button" onClick={() => setEditando(true)} className="text-xs font-medium text-brand-blue hover:underline">Cambiar</button>
        <span className="text-[11px] text-content-3">
          {modo === 'commission'
            ? 'Su pago no depende de las horas: sale de los pagos de sus alumnas y se entrega pago por pago desde Nómina.'
            : 'Es lo que la nómina multiplica por las horas de sus clases.'}
        </span>
      </div>
    );
  }

  return (
    <div className="mb-3 space-y-2.5 rounded-xl border border-line bg-canvas px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-content-3">Cómo cobra</span>
        <button type="button" onClick={() => setModo('hourly')} className={tile(modo === 'hourly')}>Por hora</button>
        <button type="button" onClick={() => setModo('commission')} className={tile(modo === 'commission')}>Comisión</button>
      </div>

      {modo === 'hourly' ? (
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-[11px] text-content-3">Tarifa por hora
            <div className="mt-1 flex items-center gap-1"><span className="text-sm text-content-3">$</span>
              <input type="number" min="0" step="10" value={rate} onChange={(e) => setRate(e.target.value)} className={inp} />
            </div>
          </label>
          <label className="text-[11px] text-content-3">Tarifa de mañana <span className="normal-case">(opcional)</span>
            <div className="mt-1 flex items-center gap-1"><span className="text-sm text-content-3">$</span>
              <input type="number" min="0" step="10" value={manana} onChange={(e) => setManana(e.target.value)} placeholder="—" className={inp} />
            </div>
          </label>
          <label className="text-[11px] text-content-3">Hasta las
            <div className="mt-1 flex items-center gap-1"><Clock size={13} className="text-content-3" />
              <input type="time" value={hasta} onChange={(e) => setHasta(e.target.value)} className={`${inp} w-28`} />
            </div>
          </label>
        </div>
      ) : (
        <label className="block text-[11px] text-content-3">Porcentaje de comisión
          <div className="mt-1 flex items-center gap-1">
            <input type="number" min="1" max="100" step="1" value={pct} onChange={(e) => setPct(e.target.value)} className={inp} />
            <span className="text-sm text-content-3">% de lo que pagan sus alumnas</span>
          </div>
        </label>
      )}

      <div className="flex items-center gap-2">
        <button type="button" onClick={guardar} disabled={guardando}
          className="rounded-lg bg-accent-solid px-3 py-1.5 text-xs font-semibold text-on-accent disabled:opacity-60">
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
        <button type="button" onClick={() => setEditando(false)} disabled={guardando}
          className="rounded-lg border border-line px-3 py-1.5 text-xs text-content-2">Cancelar</button>
      </div>
    </div>
  );
}
